const MUSIC_ENABLED_KEY = 'wordcraft.backgroundMusic';
const MUSIC_VOLUME_KEY = 'wordcraft.musicVolume';
const DEFAULT_VOLUME = 25;
const pageHasFocus = () => !document.hidden && document.hasFocus();

function createVolumeControl(sounds, initialVolume) {
  const fraction = initialVolume / 100;
  for (const sound of sounds) sound.volume = fraction;

  // iOS does not apply HTMLMediaElement.volume, so route its audio through a gain node.
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!isIOS || !AudioContextClass) {
    return { set: value => { for (const sound of sounds) sound.volume = value / 100; }, resume: async () => {}, ready: () => true };
  }

  try {
    const context = new AudioContextClass();
    const gain = context.createGain();
    gain.gain.value = fraction;
    gain.connect(context.destination);
    const directSounds = [];
    for (const sound of sounds) {
      try {
        context.createMediaElementSource(sound).connect(gain);
        sound.volume = 1;
      } catch {
        directSounds.push(sound);
      }
    }
    return {
      set: value => {
        gain.gain.value = value / 100;
        for (const sound of directSounds) sound.volume = value / 100;
      },
      resume: () => context.state === 'running' ? Promise.resolve() : context.resume(),
      ready: () => context.state === 'running',
    };
  } catch {
    return { set: value => { for (const sound of sounds) sound.volume = value / 100; }, resume: async () => {}, ready: () => true };
  }
}

export function setupBackgroundMusic() {
  const audio = document.getElementById('background-music');
  const toggle = document.getElementById('background-music-toggle');
  const volume = document.getElementById('background-music-volume');
  const volumeValue = document.getElementById('background-music-volume-value');
  const sounds = [audio, document.getElementById('correct-word-sound'), document.getElementById('incorrect-word-sound'), document.getElementById('letter-selection-sound'), document.getElementById('letter-removal-sound'), document.getElementById('refresh-sound'), document.getElementById('next-round-sound')];
  let enabled = localStorage.getItem(MUSIC_ENABLED_KEY) !== 'false';
  const storedVolume = Number(localStorage.getItem(MUSIC_VOLUME_KEY));
  const initialVolume = Number.isFinite(storedVolume) && localStorage.getItem(MUSIC_VOLUME_KEY) !== null
    ? Math.max(0, Math.min(100, storedVolume)) : DEFAULT_VOLUME;

  audio.loop = true;
  const volumeControl = createVolumeControl(sounds, initialVolume);
  toggle.checked = enabled;
  volume.value = String(initialVolume);
  volumeValue.textContent = `${initialVolume}%`;
  volume.disabled = false;

  function waitForGesture() {
    document.addEventListener('pointerdown', tryPlay, { capture: true });
    document.addEventListener('keydown', tryPlay, { capture: true });
  }

  function stopWaitingForGesture() {
    document.removeEventListener('pointerdown', tryPlay, { capture: true });
    document.removeEventListener('keydown', tryPlay, { capture: true });
  }

  async function tryPlay() {
    if (!pageHasFocus()) return;
    if (!enabled && volumeControl.ready()) { stopWaitingForGesture(); return; }
    try {
      const resume = volumeControl.resume();
      const playback = enabled && audio.paused ? audio.play() : Promise.resolve();
      await Promise.all([resume, playback]);
      if (volumeControl.ready() && (!enabled || !audio.paused)) stopWaitingForGesture();
      if (!enabled || !pageHasFocus()) audio.pause();
    } catch {
      // Browsers can require a user gesture before starting audible media.
      if (enabled) waitForGesture();
    }
  }

  toggle.addEventListener('change', () => {
    enabled = toggle.checked;
    localStorage.setItem(MUSIC_ENABLED_KEY, String(enabled));
    if (enabled) tryPlay();
    else {
      stopWaitingForGesture();
      audio.pause();
    }
  });

  function updateVolume() {
    volumeControl.set(Number(volume.value));
    volumeValue.textContent = `${volume.value}%`;
    localStorage.setItem(MUSIC_VOLUME_KEY, volume.value);
  }
  volume.addEventListener('input', updateVolume);
  volume.addEventListener('change', updateVolume);

  function pauseForFocusLoss() {
    for (const sound of sounds) sound.pause();
    if (enabled || !volumeControl.ready()) waitForGesture();
  }

  window.addEventListener('pageshow', tryPlay);
  window.addEventListener('focus', tryPlay);
  window.addEventListener('blur', pauseForFocusLoss);
  window.addEventListener('pagehide', pauseForFocusLoss);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pauseForFocusLoss();
    else tryPlay();
  });
  waitForGesture();
  tryPlay();
}

export function playCorrectWordSound() {
  if (!pageHasFocus()) return;
  const sound = document.getElementById('correct-word-sound');
  if (sound.readyState > 0) sound.currentTime = 0;
  sound.play().catch(() => {
    // Audio may be blocked until the browser has received a user gesture.
  });
}

export function playIncorrectWordSound() {
  if (!pageHasFocus()) return;
  const sound = document.getElementById('incorrect-word-sound');
  if (sound.readyState > 0) sound.currentTime = 0;
  sound.play().catch(() => {
    // Audio playback must not interrupt displaying the round result.
  });
}

export function playLetterSelectionSound() {
  if (!pageHasFocus()) return;
  const sound = document.getElementById('letter-selection-sound');
  if (sound.readyState > 0) sound.currentTime = 0;
  sound.play().catch(() => {
    // A blocked or unavailable sound must never interrupt letter selection.
  });
}

export function playLetterRemovalSound() {
  if (!pageHasFocus()) return;
  const sound = document.getElementById('letter-removal-sound');
  if (sound.readyState > 0) sound.currentTime = 0;
  sound.play().catch(() => {
    // A blocked or unavailable sound must never interrupt editing the word.
  });
}

export function playRefreshSound() {
  if (!pageHasFocus()) return;
  const sound = document.getElementById('refresh-sound');
  if (sound.readyState > 0) sound.currentTime = 0;
  sound.play().catch(() => {
    // A blocked or unavailable sound must not interrupt the board update.
  });
}

export function playNextRoundSound() {
  if (!pageHasFocus()) return;
  const sound = document.getElementById('next-round-sound');
  if (sound.readyState > 0) sound.currentTime = 0;
  sound.play().catch(() => {
    // Audio playback must not interrupt starting the next round.
  });
}
